import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  User, Shield, Phone, ChevronRight,
  Camera, Eye, EyeOff, ArrowLeft, Plus,
  CheckCircle, XCircle, Clock, Wallet, History, AlertTriangle,
  Save, Edit3, X, LogOut, CreditCard,
  MapPin, Trash2, Heart, Lock, Search, MessageCircle, Send, FileText,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuth } from '@contexts/AuthContext'
import { usePatientAuth } from '@hooks/usePatientAuth'
import {
  patientProfileService,
  type PatientProfile,
  type Transaction,
  type PatientAddress,
  type EmergencyContact,
} from '@lib/supabase/patientProfile.service'
import { queryKeys } from '@lib/queryKeys'
import { analyticsService, getAmountBucket } from '@lib/analytics/posthog.service'
import Button from '@components/web/Button'
import ConfirmationModal from '@components/web/ConfirmationModal'

// ---- helpers ----------------------------------------------------------------
function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
  }).format(n)
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getInitials(name?: string | null) {
  if (!name) return '?'
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

const TX_TYPE_STYLE: Record<string, { label: string; color: string; bg: string; sign: string }> = {
  topup:       { label: 'Top Up',  color: 'text-emerald-600', bg: 'bg-emerald-50', sign: '+' },
  debit:       { label: 'Debit',   color: 'text-red-600',     bg: 'bg-red-50',     sign: '-' },
  refund:      { label: 'Refund',  color: 'text-blue-600',    bg: 'bg-blue-50',    sign: '+' },
  appointment: { label: 'Payment', color: 'text-red-600',     bg: 'bg-red-50',     sign: '-' },
  credit:      { label: 'Credit',  color: 'text-emerald-600', bg: 'bg-emerald-50', sign: '+' },
}

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say']
const EMERGENCY_RELATIONSHIP_OPTIONS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Doctor', 'Neighbor', 'Colleague', 'Other']
const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000]

type ActiveView = 'main' | 'personal' | 'wallet' | 'address' | 'emergency-contacts' | 'security' | 'support' | 'terms'

// ============================================================================
// Personal Details Panel
// ============================================================================
function PersonalDetailsPanel({
  profile,
  onBack,
  onSaved,
}: {
  profile: PatientProfile
  onBack: () => void
  onSaved: () => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [form, setForm] = useState({
    full_name: profile.full_name ?? '',
    phone_number: profile.phone_number ?? '',
    date_of_birth: profile.date_of_birth ?? '',
    gender: profile.gender ?? '',
  })

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    const result = await patientProfileService.updateProfile({
      full_name: form.full_name || null,
      phone_number: form.phone_number || null,
      date_of_birth: form.date_of_birth || null,
      gender: form.gender || null,
    })
    setIsSaving(false)
    if (result.success) {
      setIsEditing(false)
      onSaved()
    } else {
      setSaveError(result.error ?? 'Failed to save changes.')
    }
  }

  type FormKey = keyof typeof form

  const Field = ({
    label,
    field,
    type = 'text',
    options,
  }: {
    label: string
    field: FormKey
    type?: string
    options?: string[]
  }) => (
    <div className="space-y-1.5">
      <label htmlFor={`patient-profile-${String(field)}`} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {options ? (
        <select
          id={`patient-profile-${String(field)}`}
          title={label}
          aria-label={label}
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-slate-50 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
          value={form[field]}
          onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
          disabled={!isEditing}
        >
          <option value="">Select…</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          id={`patient-profile-${String(field)}`}
          type={type}
          title={label}
          aria-label={label}
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-slate-50 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:bg-slate-100"
          value={form[field]}
          onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
          disabled={!isEditing}
        />
      )}
    </div>
  )

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button type="button" title="Go back" aria-label="Go back" onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted">
          <ArrowLeft size={18} />
        </button>
        <h2 className="font-semibold text-foreground flex-1">Personal Details</h2>
        {isEditing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setSaveError(null) }}>
              Cancel
            </Button>
            <Button size="sm" isLoading={isSaving} onClick={handleSave}>
              <Save size={14} className="mr-1.5" /> Save
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Edit3 size={14} className="mr-1.5" /> Edit
          </Button>
        )}
      </div>

      <div className="bg-card border border-border p-5 space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</label>
          <div className="px-3 py-2.5 rounded-xl bg-muted text-sm text-muted-foreground">
            {profile.email ?? '—'}
          </div>
        </div>
        <Field label="Full Name" field="full_name" />
        <Field label="Phone Number" field="phone_number" type="tel" />
        <Field label="Date of Birth" field="date_of_birth" type="date" />
        <Field label="Gender" field="gender" options={GENDERS} />
      </div>

      {saveError && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
          <AlertTriangle size={14} /> {saveError}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Top-Up Modal
// ============================================================================
function TopUpModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const MIN_TOPUP_AMOUNT = 1000
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stage, setStage] = useState<'input' | 'polling' | 'done'>('input')
  const [pollResult, setPollResult] = useState<'success' | 'failed' | null>(null)
  const pollingRef = useRef(false)
  const referenceRef = useRef<string | null>(null)

  const amount = selectedAmount ?? (customAmount ? Number(customAmount) : 0)
  const isAmountValid = amount >= MIN_TOPUP_AMOUNT

  const startPolling = useCallback(async (reference: string) => {
    pollingRef.current = true
    setStage('polling')
    const maxAttempts = 30
    let attempts = 0
    while (pollingRef.current && attempts < maxAttempts) {
      await new Promise((r) => setTimeout(r, 3000))
      attempts++
      const status = await patientProfileService.getPaymentIntentStatus(reference)
      if (status.status === 'success') {
        analyticsService.capture('topup_completed', {
          source: 'patient_profile_screen',
          stage: 'payment_confirmed',
          amount_bucket: getAmountBucket(amount),
          platform: 'web',
        })
        setPollResult('success')
        setStage('done')
        pollingRef.current = false
        onSuccess()
        return
      }
      if (status.status === 'failed' || status.status === 'abandoned') {
        analyticsService.capture('topup_failed', {
          source: 'patient_profile_screen',
          stage: 'payment_polling',
          error_type: status.status,
          amount_bucket: getAmountBucket(amount),
          platform: 'web',
        })
        setPollResult('failed')
        setStage('done')
        pollingRef.current = false
        return
      }
    }
    setPollResult('failed')
    setStage('done')
    pollingRef.current = false
  }, [onSuccess, amount])

  useEffect(() => () => { pollingRef.current = false }, [])

  const handleInitiate = async () => {
    if (amount < MIN_TOPUP_AMOUNT) { setError(`Minimum top-up is ₦${MIN_TOPUP_AMOUNT.toLocaleString()}.`); return }
    setIsLoading(true)
    setError(null)
    analyticsService.capture('topup_started', {
      source: 'patient_profile_screen',
      amount_bucket: getAmountBucket(amount),
      platform: 'web',
    })
    const result = await patientProfileService.initializePaystackTopup(amount)
    setIsLoading(false)
    if (!result.success || !result.authorizationUrl) {
      analyticsService.capture('topup_failed', {
        source: 'patient_profile_screen',
        stage: 'initialize',
        error_type: 'initialize_failed',
        amount_bucket: getAmountBucket(amount),
        platform: 'web',
      })
      setError(result.error ?? 'Failed to initialize payment.')
      return
    }
    referenceRef.current = result.reference ?? null
    window.open(result.authorizationUrl, '_blank', 'noopener,noreferrer')
    if (result.reference) startPolling(result.reference)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border w-full max-w-sm shadow-xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground">Add Funds</h3>
          <button type="button" title="Close add funds modal" aria-label="Close add funds modal" onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>

        {stage === 'input' && (
          <>
            <p className="text-sm text-muted-foreground">Select or enter an amount to top up your wallet.</p>
            <div className="grid grid-cols-3 gap-2">
              {QUICK_AMOUNTS.map((a) => (
                <button
                  key={a}
                  onClick={() => { setSelectedAmount(a); setCustomAmount('') }}
                  className={clsx(
                    'py-2 rounded-xl text-sm font-medium border transition-all',
                    selectedAmount === a
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border text-foreground hover:bg-muted'
                  )}
                >
                  {formatCurrency(a)}
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Custom Amount (₦)</label>
              <input
                type="number"
                min={MIN_TOPUP_AMOUNT}
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null) }}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className={clsx('text-xs', isAmountValid || amount === 0 ? 'text-muted-foreground' : 'text-destructive')}>
                Minimum top-up amount is {formatCurrency(MIN_TOPUP_AMOUNT)}.
              </p>
            </div>
            {error && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertTriangle size={13} /> {error}
              </p>
            )}
            <Button
              className="w-full disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted disabled:opacity-100"
              isLoading={isLoading}
              onClick={handleInitiate}
              disabled={!isAmountValid}
            >
              Add {isAmountValid ? formatCurrency(amount) : ''}
            </Button>
          </>
        )}

        {stage === 'polling' && (
          <div className="text-center py-6 space-y-3">
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm font-medium text-foreground">Waiting for payment confirmation…</p>
            <p className="text-xs text-muted-foreground">Complete the payment in the opened tab.</p>
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          </div>
        )}

        {stage === 'done' && (
          <div className="text-center py-6 space-y-4">
            {pollResult === 'success' ? (
              <>
                <CheckCircle size={44} className="text-emerald-500 mx-auto" />
                <p className="font-semibold text-foreground">Payment Successful!</p>
                <p className="text-sm text-muted-foreground">Your wallet has been credited.</p>
                <Button className="w-full" onClick={onClose}>Done</Button>
              </>
            ) : (
              <>
                <XCircle size={44} className="text-red-500 mx-auto" />
                <p className="font-semibold text-foreground">Payment Not Confirmed</p>
                <p className="text-sm text-muted-foreground">If you completed the payment, refresh to check your balance.</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
                  <Button className="flex-1" onClick={() => { setStage('input'); setPollResult(null) }}>Try Again</Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Wallet Panel
// ============================================================================
const TX_PAGE_SIZE = 10

function WalletPanel({ onBack }: { onBack: () => void }) {
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()
  const [showBalance, setShowBalance] = useState(true)
  const [showTopUp, setShowTopUp] = useState(false)

  const balanceQuery = useQuery({
    queryKey: queryKeys.patient.walletBalance(patient?.id),
    queryFn: patientProfileService.getWalletBalance,
    enabled: !!patient?.id,
  })

  const transactionsQuery = useInfiniteQuery({
    queryKey: queryKeys.patient.walletTransactions(patient?.id, TX_PAGE_SIZE),
    queryFn: ({ pageParam = 0 }) => patientProfileService.getTransactions(pageParam, TX_PAGE_SIZE),
    enabled: !!patient?.id,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => lastPage.length === TX_PAGE_SIZE ? allPages.length : undefined,
  })

  const balance = balanceQuery.data ?? 0
  const transactions = useMemo(
    () => (transactionsQuery.data?.pages ?? []).flatMap((page) => page),
    [transactionsQuery.data],
  )
  const isLoading = balanceQuery.isPending || transactionsQuery.isPending
  const isLoadingMore = transactionsQuery.isFetchingNextPage
  const hasMore = Boolean(transactionsQuery.hasNextPage)

  const loadMore = async () => {
    if (!hasMore || isLoadingMore) return
    await transactionsQuery.fetchNextPage()
  }

  const handleTopUpSuccess = async () => {
    await Promise.all([
      balanceQuery.refetch(),
      transactionsQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: queryKeys.patient.walletBalance(patient?.id) }),
    ])
  }

  const txMeta = (tx: Transaction) =>
    TX_TYPE_STYLE[tx.type] ?? { label: tx.type, color: 'text-foreground', bg: 'bg-muted', sign: '' }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button type="button" title="Go back" aria-label="Go back" onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted">
          <ArrowLeft size={18} />
        </button>
        <h2 className="font-semibold text-foreground flex-1">Payment Wallet</h2>
      </div>

      <div className="bg-primary p-5 text-primary-foreground space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm opacity-80">Wallet Balance</p>
          <button onClick={() => setShowBalance(!showBalance)} className="opacity-80 hover:opacity-100">
            {showBalance ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {isLoading ? (
          <div className="h-9 w-32 bg-white/20 rounded-lg animate-pulse" />
        ) : (
          <p className="text-3xl font-bold">
            {showBalance ? formatCurrency(balance) : '••••••'}
          </p>
        )}
        <Button
          size="sm"
          className="bg-white !text-primary hover:bg-white/90 border-0 font-semibold"
          onClick={() => setShowTopUp(true)}
        >
          <Plus size={14} className="mr-1.5" /> Add Funds
        </Button>
      </div>

      <div className="bg-card border border-border overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
          <History size={16} className="text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Transaction History</h3>
        </div>
        {isLoading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
                <div className="h-4 bg-muted rounded w-16" />
              </div>
            ))}
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-10 text-center">
            <Wallet size={32} className="text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No transactions yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {transactions.map((tx) => {
              const meta = txMeta(tx)
              return (
                <div key={tx.id} className="flex items-center gap-3 px-5 py-3.5">
                  <div className={clsx('w-10 h-10 rounded-full flex items-center justify-center shrink-0', meta.bg)}>
                    {meta.sign === '+' ? (
                      <Plus size={16} className={meta.color} />
                    ) : (
                      <CreditCard size={16} className={meta.color} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {tx.description ?? meta.label}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={clsx('text-sm font-semibold', meta.color)}>
                      {meta.sign}{formatCurrency(tx.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">{tx.status}</p>
                  </div>
                </div>
              )
            })}
            {hasMore && (
              <div className="px-5 py-3">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="w-full text-sm text-primary font-medium py-1.5 hover:text-primary/80 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoadingMore ? (
                    <><div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Loading…</>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showTopUp && (
        <TopUpModal
          onClose={() => setShowTopUp(false)}
          onSuccess={() => { void handleTopUpSuccess() }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Panel Header (reusable)
// ============================================================================
function PanelHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-3">
      <button type="button" title="Go back" aria-label="Go back" onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted">
        <ArrowLeft size={18} />
      </button>
      <h2 className="font-semibold text-foreground flex-1">{title}</h2>
    </div>
  )
}

// ============================================================================
// Address Panel
// ============================================================================
function AddressPanel({ onBack }: { onBack: () => void }) {
  const { session, role } = useAuth()
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingDeleteAddress, setPendingDeleteAddress] = useState<PatientAddress | null>(null)
  const [isDeletingAddress, setIsDeletingAddress] = useState(false)
  const emptyForm = { label: '', address_line1: '', address_line2: '', city: '', state: '', country: 'Nigeria', is_default: false }
  const [form, setForm] = useState(emptyForm)
  const addressQueryOwner = session?.user.id ?? patient?.auth_id ?? patient?.id ?? null

  const addressesQuery = useQuery({
    queryKey: queryKeys.patient.addresses(addressQueryOwner),
    queryFn: patientProfileService.getAddresses,
    enabled: !!session && role === 'patient',
  })

  const addresses = addressesQuery.data ?? []
  const isLoading = addressesQuery.isPending

  const refreshAddresses = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.addresses(addressQueryOwner) })
    await addressesQuery.refetch()
  }

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowAddForm(true); setFormError(null) }
  const openEdit = (a: PatientAddress) => {
    setForm({ label: a.label ?? '', address_line1: a.address_line1, address_line2: a.address_line2 ?? '', city: a.city, state: a.state, country: a.country, is_default: a.is_default })
    setEditingId(a.id); setShowAddForm(true); setFormError(null)
  }

  const handleSave = async () => {
    if (!form.address_line1.trim() || !form.city.trim() || !form.state.trim()) { setFormError('Address, city and state are required.'); return }
    setSaving(true); setFormError(null)
    const result = editingId
      ? await patientProfileService.updateAddress(editingId, form)
      : await patientProfileService.addAddress(form)
    setSaving(false)
    if (result.success) { setShowAddForm(false); await refreshAddresses() } else { setFormError(result.error ?? 'Failed to save.') }
  }

  const handleDeleteRequest = (address: PatientAddress) => {
    setPendingDeleteAddress(address)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDeleteAddress) return
    setIsDeletingAddress(true)
    const result = await patientProfileService.deleteAddress(pendingDeleteAddress.id)
    setIsDeletingAddress(false)
    if (result.success) {
      setPendingDeleteAddress(null)
      await refreshAddresses()
      return
    }
    alert(result.error ?? 'Failed to delete address.')
  }

  const handleSetDefault = async (id: string) => {
    await patientProfileService.setDefaultAddress(id); await refreshAddresses()
  }

  const renderAddressForm = () => (
    <div className="bg-card border border-border p-5 space-y-4">
      <p className="text-sm font-semibold text-foreground">{editingId ? 'Edit Address' : 'Add New Address'}</p>
      {[
        { label: 'Address Line *', key: 'address_line1', required: true },
        { label: 'City *', key: 'city', required: true },
        { label: 'State *', key: 'state', required: true },
        { label: 'Country', key: 'country', required: false },
      ].map(({ label, key }) => (
        <div key={key} className="space-y-1">
          <label htmlFor={`patient-address-${key}`} className="text-xs text-muted-foreground">{label}</label>
          <input
            id={`patient-address-${key}`}
            title={label}
            aria-label={label}
            value={(form as any)[key]}
            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      ))}
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} className="rounded" />
        <span className="text-sm text-foreground">Set as default address</span>
      </label>
      {formError && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle size={12} />{formError}</p>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowAddForm(false)}>Cancel</Button>
        <Button size="sm" className="flex-1" isLoading={saving} onClick={handleSave}>Save</Button>
      </div>
    </div>
  )

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button type="button" title="Go back" aria-label="Go back" onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted"><ArrowLeft size={18} /></button>
        <h2 className="font-semibold text-foreground flex-1">Address</h2>
        {!showAddForm && <Button size="sm" variant="outline" onClick={openAdd}><Plus size={14} className="mr-1" />Add</Button>}
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1,2].map(i=><div key={i} className="h-28 bg-muted animate-pulse"/>)}</div>
      ) : showAddForm ? (
        renderAddressForm()
      ) : addresses.length === 0 ? (
        <div className="bg-card border border-border p-8 text-center">
          <MapPin size={32} className="text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No addresses added yet.</p>
          <Button size="sm" className="mt-3" onClick={openAdd}><Plus size={14} className="mr-1"/>Add Address</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map((a) => (
            <div key={a.id} className="bg-card border border-border p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><MapPin size={17} className="text-blue-500" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{a.label || 'Address'}</p>
                    {a.is_default && <span className="text-xs bg-primary/10 text-primary font-medium px-2 py-0.5 rounded-full">Default</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}<br/>
                    {a.city}, {a.state}, {a.country}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 pt-1 border-t border-border">
                {!a.is_default && (
                  <button onClick={() => handleSetDefault(a.id)} className="text-xs text-primary font-medium hover:text-primary/80">Set as Default</button>
                )}
                <button onClick={() => openEdit(a)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 ml-auto"><Edit3 size={12}/>Edit</button>
                <button onClick={() => handleDeleteRequest(a)} className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-1"><Trash2 size={12}/>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingDeleteAddress && (
        <ConfirmationModal
          title="Delete Address"
          message={`Are you sure you want to delete \"${pendingDeleteAddress.label || pendingDeleteAddress.address_line1}\"? This action cannot be undone.`}
          confirmLabel="Delete Address"
          confirmVariant="destructive"
          isLoading={isDeletingAddress}
          onCancel={() => setPendingDeleteAddress(null)}
          onConfirm={() => { void handleConfirmDelete() }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Emergency Contacts Panel
// ============================================================================
function EmergencyContactsPanel({ onBack }: { onBack: () => void }) {
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingDeleteContact, setPendingDeleteContact] = useState<EmergencyContact | null>(null)
  const [isDeletingContact, setIsDeletingContact] = useState(false)
  const emptyForm = { name: '', relationship: '', phone_number: '', is_primary: false }
  const [form, setForm] = useState(emptyForm)

  const emergencyContactsQuery = useQuery({
    queryKey: queryKeys.patient.emergencyContacts(patient?.id),
    queryFn: patientProfileService.getEmergencyContacts,
    enabled: !!patient?.id,
  })

  const contacts = emergencyContactsQuery.data ?? []
  const isLoading = emergencyContactsQuery.isPending

  const refreshEmergencyContacts = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.emergencyContacts(patient?.id) })
    await emergencyContactsQuery.refetch()
  }

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); setFormError(null) }
  const openEdit = (c: EmergencyContact) => {
    setForm({ name: c.name, relationship: c.relationship, phone_number: c.phone_number, is_primary: c.is_primary })
    setEditingId(c.id); setShowForm(true); setFormError(null)
  }

  const handleSave = async () => {
    if (!form.name.trim() || !form.relationship.trim() || !form.phone_number.trim()) { setFormError('Name, relationship and phone are required.'); return }
    setSaving(true); setFormError(null)
    const result = editingId
      ? await patientProfileService.updateEmergencyContact(editingId, form)
      : await patientProfileService.addEmergencyContact(form)
    setSaving(false)
    if (result.success) { setShowForm(false); await refreshEmergencyContacts() } else { setFormError(result.error ?? 'Failed to save.') }
  }

  const handleDeleteRequest = (contact: EmergencyContact) => {
    setPendingDeleteContact(contact)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDeleteContact) return
    setIsDeletingContact(true)
    const result = await patientProfileService.deleteEmergencyContact(pendingDeleteContact.id)
    setIsDeletingContact(false)
    if (result.success) {
      setPendingDeleteContact(null)
      await refreshEmergencyContacts()
      return
    }
    alert(result.error ?? 'Failed to remove emergency contact.')
  }

  const handleSetPrimary = async (id: string) => {
    await patientProfileService.setPrimaryEmergencyContact(id); await refreshEmergencyContacts()
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button type="button" title="Go back" aria-label="Go back" onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted"><ArrowLeft size={18} /></button>
        <h2 className="font-semibold text-foreground flex-1">Emergency Contacts</h2>
        {!showForm && <Button size="sm" variant="outline" onClick={openAdd}><Plus size={14} className="mr-1"/>Add</Button>}
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1,2].map(i=><div key={i} className="h-28 bg-muted animate-pulse"/>)}</div>
      ) : showForm ? (
        <div className="bg-card border border-border p-5 space-y-4">
          <p className="text-sm font-semibold text-foreground">{editingId ? 'Edit Contact' : 'Add Emergency Contact'}</p>
          {[
            { label: 'Full Name *', key: 'name', type: 'text' },
            { label: 'Phone Number *', key: 'phone_number', type: 'tel' },
          ].map(({ label, key, type }) => (
            <div key={key} className="space-y-1">
              <label htmlFor={`patient-emergency-${key}`} className="text-xs text-muted-foreground">{label}</label>
              <input
                id={`patient-emergency-${key}`}
                type={type}
                title={label}
                aria-label={label}
                value={(form as any)[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          ))}
          <div className="space-y-1">
            <label htmlFor="patient-emergency-relationship" className="text-xs text-muted-foreground">Relationship *</label>
            <select
              id="patient-emergency-relationship"
              title="Relationship"
              aria-label="Relationship"
              value={form.relationship}
              onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select relationship</option>
              {EMERGENCY_RELATIONSHIP_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked }))} className="rounded"/>
            <span className="text-sm text-foreground">Set as primary contact</span>
          </label>
          {formError && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle size={12}/>{formError}</p>}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" className="flex-1" isLoading={saving} onClick={handleSave}>Save</Button>
          </div>
        </div>
      ) : contacts.length === 0 ? (
        <div className="bg-card border border-border p-8 text-center">
          <Phone size={32} className="text-muted-foreground/40 mx-auto mb-2"/>
          <p className="text-sm text-muted-foreground">No emergency contacts added yet.</p>
          <Button size="sm" className="mt-3" onClick={openAdd}><Plus size={14} className="mr-1"/>Add Contact</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div key={c.id} className="bg-card border border-border p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center shrink-0"><User size={17} className="text-rose-500"/></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{c.name}</p>
                    {c.is_primary && (
                      <span className="inline-flex items-center gap-1 text-xs bg-rose-50 text-rose-600 font-medium px-2 py-0.5 rounded-full">
                        <Heart size={10}/>Primary
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.relationship}</p>
                  <p className="text-xs text-muted-foreground">{c.phone_number}</p>
                </div>
              </div>
              <div className="flex gap-2 pt-1 border-t border-border">
                {!c.is_primary && (
                  <button type="button" onClick={() => handleSetPrimary(c.id)} className="text-xs text-primary font-medium hover:text-primary/80">Set as Primary</button>
                )}
                <button type="button" onClick={() => openEdit(c)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 ml-auto"><Edit3 size={12}/>Edit</button>
                <button type="button" onClick={() => handleDeleteRequest(c)} className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-1"><Trash2 size={12}/>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingDeleteContact && (
        <ConfirmationModal
          title="Remove Emergency Contact"
          message={`Are you sure you want to remove ${pendingDeleteContact.name}? This action cannot be undone.`}
          confirmLabel="Remove Contact"
          confirmVariant="destructive"
          isLoading={isDeletingContact}
          onCancel={() => setPendingDeleteContact(null)}
          onConfirm={() => { void handleConfirmDelete() }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Privacy & Security Panel
// ============================================================================
function SecurityPanel({ onBack }: { onBack: () => void }) {
  const [showPwForm, setShowPwForm] = useState(false)
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' })
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirm: false })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleChangePassword = async () => {
    if (!pwForm.current) { setError('Enter your current password.'); return }
    if (pwForm.newPw.length < 8) { setError('New password must be at least 8 characters.'); return }
    if (pwForm.newPw !== pwForm.confirm) { setError('New passwords do not match.'); return }
    setSaving(true); setError(null)
    const result = await patientProfileService.changePassword(pwForm.current, pwForm.newPw)
    setSaving(false)
    if (result.success) { setSuccess(true); setShowPwForm(false); setPwForm({ current: '', newPw: '', confirm: '' }) }
    else setError(result.error ?? 'Failed to change password.')
  }

  return (
    <div className="space-y-5">
      <PanelHeader title="Privacy & Security" onBack={onBack} />

      {success && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
          <CheckCircle size={16}/> Password updated successfully.
        </div>
      )}

      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Security</p>
        <button
          onClick={() => { setShowPwForm(!showPwForm); setError(null); setSuccess(false) }}
          className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-muted transition-colors border-t border-border text-left"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0"><Lock size={17} className="text-amber-600"/></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">Change Password</p>
            <p className="text-xs text-muted-foreground mt-0.5">Update your account password</p>
          </div>
          <ChevronRight size={16} className={clsx('text-muted-foreground transition-transform', showPwForm && 'rotate-90')}/>
        </button>

        {showPwForm && (
          <div className="px-5 pb-5 space-y-4 border-t border-border pt-4">
            {[
              { label: 'Current Password', key: 'current' as const },
              { label: 'New Password', key: 'newPw' as const },
              { label: 'Confirm New Password', key: 'confirm' as const },
            ].map(({ label, key }) => (
              <div key={key} className="space-y-1">
                <label htmlFor={`patient-password-${key}`} className="text-xs text-muted-foreground">{label}</label>
                <div className="relative">
                  <input
                    id={`patient-password-${key}`}
                    type={showPw[key] ? 'text' : 'password'}
                    title={label}
                    aria-label={label}
                    value={pwForm[key]}
                    onChange={(e) => setPwForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button type="button" onClick={() => setShowPw((s) => ({ ...s, [key]: !s[key] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw[key] ? <EyeOff size={14}/> : <Eye size={14}/>}
                  </button>
                </div>
              </div>
            ))}
            {error && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle size={12}/>{error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { setShowPwForm(false); setError(null) }}>Cancel</Button>
              <Button size="sm" className="flex-1" isLoading={saving} onClick={handleChangePassword}>Update Password</Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-muted/60 border border-border p-4 text-xs text-muted-foreground leading-relaxed">
        <p className="font-medium text-foreground mb-1">Security Tips</p>
        <p>• Use a unique password you don't use elsewhere</p>
        <p>• Your password must be at least 8 characters</p>
        <p>• Never share your login credentials with anyone</p>
      </div>
    </div>
  )
}

// ============================================================================
// Patient Support Panel
// ============================================================================
function PatientSupportPanel({ onBack }: { onBack: () => void }) {
  const { patient } = usePatientAuth()
  const [search, setSearch] = useState('')
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState({ subject: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fbSuccess, setFbSuccess] = useState(false)
  const [fbError, setFbError] = useState<string | null>(null)

  const supportQuery = useQuery({
    queryKey: queryKeys.patient.support(patient?.id),
    queryFn: async () => {
      const [config, faqs] = await Promise.all([
        patientProfileService.getPatientAppConfig(),
        patientProfileService.getPatientFAQs(),
      ])

      return { config, faqs }
    },
    enabled: !!patient?.id,
  })

  const config = supportQuery.data?.config ?? null
  const faqs = supportQuery.data?.faqs ?? []
  const isLoading = supportQuery.isPending

  const handleSubmitFeedback = async () => {
    if (!feedback.message.trim()) { setFbError('Please enter a message'); return }
    setIsSubmitting(true); setFbError(null)
    const result = await patientProfileService.submitPatientFeedback({ subject: feedback.subject, message: feedback.message })
    setIsSubmitting(false)
    if (result.success) { setFbSuccess(true); setFeedback({ subject: '', message: '' }) }
    else setFbError(result.error ?? 'Failed to submit')
  }

  const filteredFAQs = faqs.filter((f) =>
    f.title.toLowerCase().includes(search.toLowerCase()) ||
    f.content.toLowerCase().includes(search.toLowerCase())
  )

  const phone = config?.supportPhone ?? '+2349132371175'
  const whatsapp = config?.supportWhatsApp ?? '+2349132371175'

  return (
    <div className="space-y-5">
      <PanelHeader title="Patient Support" onBack={onBack} />

      {isLoading ? (
        <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="h-20 bg-muted animate-pulse"/>)}</div>
      ) : (
        <>
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Get Help</p>
            <a href={`tel:${phone}`} className="flex items-center gap-3 bg-card border border-border p-4 hover:bg-muted/50 transition-colors no-underline">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><Phone size={18} className="text-emerald-600"/></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Call Support</p>
                <p className="text-xs text-muted-foreground">{phone} · Available 8am–8pm</p>
              </div>
              <ChevronRight size={16} className="text-muted-foreground shrink-0"/>
            </a>
            <a href={`https://wa.me/${whatsapp.replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-card border border-border p-4 hover:bg-muted/50 transition-colors no-underline">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0"><MessageCircle size={18} className="text-green-600"/></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">WhatsApp</p>
                <p className="text-xs text-muted-foreground">Chat with our support team</p>
              </div>
              <ChevronRight size={16} className="text-muted-foreground shrink-0"/>
            </a>
          </div>

          {/* Feedback */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Send Feedback</p>
            {fbSuccess ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                <CheckCircle size={16}/> Feedback submitted. Thank you!
              </div>
            ) : (
              <div className={clsx('bg-card border border-border overflow-hidden', !showFeedback && 'cursor-pointer')} onClick={() => !showFeedback && setShowFeedback(true)}>
                {showFeedback ? (
                  <div className="p-5 space-y-4">
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Subject (optional)</label>
                      <input value={feedback.subject} onChange={(e) => setFeedback((f) => ({ ...f, subject: e.target.value }))} placeholder="e.g. App issue, Booking problem…" className="w-full px-3 py-2.5 text-sm rounded-xl border border-border bg-slate-50 text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"/>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Message *</label>
                      <textarea rows={4} value={feedback.message} onChange={(e) => setFeedback((f) => ({ ...f, message: e.target.value }))} placeholder="Tell us how we can help…" className="w-full px-3 py-2.5 text-sm rounded-xl border border-border bg-slate-50 text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"/>
                    </div>
                    {fbError && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle size={12}/>{fbError}</p>}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => { setShowFeedback(false); setFbError(null) }}>Cancel</Button>
                      <Button size="sm" className="flex-1" isLoading={isSubmitting} onClick={handleSubmitFeedback}><Send size={13} className="mr-1.5"/>Submit</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4">
                    <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0"><Send size={18} className="text-sky-600"/></div>
                    <div className="flex-1"><p className="text-sm font-semibold text-foreground">Send Feedback</p><p className="text-xs text-muted-foreground">Share your thoughts or report an issue</p></div>
                    <ChevronRight size={16} className="text-muted-foreground"/>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* FAQs */}
          {faqs.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Frequently Asked Questions</p>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search FAQs…" className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"/>
              </div>
              <div className="space-y-2">
                {filteredFAQs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No results found.</p>
                ) : filteredFAQs.map((faq) => {
                  const isOpen = expandedFAQ === faq.id
                  return (
                    <div key={faq.id} className="bg-card border border-border overflow-hidden">
                      <button onClick={() => setExpandedFAQ(isOpen ? null : faq.id)} className="w-full flex items-center justify-between px-4 py-3 text-left">
                        <span className="text-sm font-medium text-foreground pr-3">{faq.title}</span>
                        <span className={clsx('text-xl font-light text-muted-foreground shrink-0 transition-transform duration-200 leading-none', isOpen && 'rotate-45')}>+</span>
                      </button>
                      {isOpen && <div className="px-4 pb-4 border-t border-border"><p className="text-sm text-muted-foreground leading-relaxed mt-3">{faq.content}</p></div>}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================================
// Patient Terms & Privacy Panel
// ============================================================================
type PatientDocContent = { title: string; content: string; updatedAt?: string } | null

const PATIENT_POLICY_DOCS = [
  { title: 'Terms of Service', description: 'Our terms and conditions for using HomiCareplus services', color: '#059669', fetchKey: 'terms' as const },
  { title: 'Privacy Policy', description: 'How we collect, use, and protect your personal information', color: '#2563EB', fetchKey: 'privacy' as const },
  { title: 'Data Usage Policy', description: 'Details about how your health data is used and shared', color: '#7C3AED', fetchKey: 'data' as const },
]

const PATIENT_POLICY_SUMMARIES = [
  { title: 'Information We Collect', content: 'We collect information you provide directly to us, such as when you create an account, book appointments, or contact us for support. This includes personal information like your name, email address, phone number, and medical information necessary for providing healthcare services.' },
  { title: 'How We Use Your Information', content: 'We use your information to provide, maintain, and improve our services, process appointments, communicate with you, ensure safety and security, and comply with legal obligations.' },
  { title: 'Information Sharing', content: 'We may share your information with healthcare providers you book appointments with, service providers who help us operate our platform, and as required by law. All sharing is done with appropriate safeguards to protect your privacy.' },
  { title: 'Data Security', content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security audits.' },
  { title: 'Your Rights', content: 'You have the right to access, update, or delete your personal information. You can also opt out of certain communications and request a copy of your data. Contact our support team to exercise these rights.' },
]

export function PatientTermsPage() {
  const { patient } = usePatientAuth()
  const [openSection, setOpenSection] = useState<string | null>(null)
  const [selectedDocKey, setSelectedDocKey] = useState<'terms' | 'privacy' | 'data'>('terms')
  const navigate = useNavigate()

  const policyDocumentsQuery = useQuery({
    queryKey: queryKeys.patient.policyDocuments(patient?.id),
    queryFn: async () => {
      const [terms, privacy, data] = await Promise.all([
        patientProfileService.getPatientTerms(),
        patientProfileService.getPatientPrivacyPolicy(),
        patientProfileService.getPatientDataUsagePolicy(),
      ])

      return { terms, privacy, data }
    },
    enabled: !!patient?.id,
  })

  const docContents: Record<string, PatientDocContent> = policyDocumentsQuery.data ?? {}
  const docsLoading = policyDocumentsQuery.isPending

  const fmtDate = (d?: string) => {
    if (!d) return 'June 15, 2026'
    try { return new Date(d).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' }) } catch { return 'June 15, 2026' }
  }

  const activeDoc = PATIENT_POLICY_DOCS.find((doc) => doc.fetchKey === selectedDocKey) ?? PATIENT_POLICY_DOCS[0]
  const activeContent = docContents[selectedDocKey] ?? { title: activeDoc.title, content: '' }

  return (
    <div className="p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button type="button" title="Back to profile" aria-label="Back to profile" onClick={() => navigate('/patient/profile')} className="p-1.5 rounded-lg hover:bg-muted">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-foreground">Terms & Conditions</h1>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-4 sm:p-5">
          <div className="flex flex-wrap gap-2">
            {PATIENT_POLICY_DOCS.map((doc) => (
              <button
                key={doc.title}
                type="button"
                onClick={() => setSelectedDocKey(doc.fetchKey)}
                className={clsx(
                  'px-3 py-2 rounded-xl border text-sm font-medium transition-colors',
                  selectedDocKey === doc.fetchKey
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                )}
              >
                {doc.title}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border overflow-hidden">
          <div className="border-b border-border px-5 py-4">
            <p className="text-base font-semibold text-foreground">{activeContent.title}</p>
            <p className="mt-1 text-xs text-muted-foreground">Last updated: {fmtDate(activeContent.updatedAt)}</p>
          </div>

          <div className="px-5 py-5">
            {activeContent.content
              ? activeContent.content.split('\n').map((line, i) => (
                  <p key={i} className={clsx('text-sm text-foreground/80 leading-relaxed', line.trim() === '' ? 'mb-3' : 'mb-1')}>{line}</p>
                ))
              : (
                <p className="text-sm text-muted-foreground">{docsLoading ? 'Loading document...' : 'Content not available.'}</p>
              )}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-lg font-semibold text-foreground">Policy Summary</p>
          {PATIENT_POLICY_SUMMARIES.map((section) => {
            const isOpen = openSection === section.title
            return (
              <div key={section.title} className="bg-card border border-border overflow-hidden">
                <button onClick={() => setOpenSection(isOpen ? null : section.title)} className="w-full flex items-center justify-between px-4 py-3.5 text-left">
                  <span className="text-sm font-semibold text-foreground pr-3">{section.title}</span>
                  <span className={clsx('text-xl font-light text-muted-foreground shrink-0 transition-transform duration-200 leading-none', isOpen && 'rotate-45')}>+</span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">{section.content}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function PatientTermsPanel({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-5">
      <PanelHeader title="Terms & Conditions" onBack={onBack} />
      <PatientTermsPage />
    </div>
  )
}

// ============================================================================
// Menu Row
// ============================================================================
function MenuRow({
  icon: Icon,
  title,
  subtitle,
  onClick,
  disabled,
  showChevron = true,
}: {
  icon: any
  title: string
  subtitle?: string
  onClick: () => void
  disabled?: boolean
  showChevron?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-muted transition-colors border-t border-border first:border-t-0 disabled:opacity-50 disabled:pointer-events-none text-left"
    >
      <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
        <Icon size={17} className="text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>}
      </div>
      {showChevron && <ChevronRight size={16} className="text-muted-foreground shrink-0" />}
    </button>
  )
}

// ============================================================================
// Main Profile Page
// ============================================================================
export default function Profile() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { logout, session, role } = useAuth()
  const { patient, isLoading: patientLoading, refreshPatient } = usePatientAuth()
  const initialView = (location.state as { view?: ActiveView } | null)?.view ?? 'main'

  const [activeView, setActiveView] = useState<ActiveView>(initialView)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [showSignOutModal, setShowSignOutModal] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const profileQueryOwner = session?.user.id ?? patient?.auth_id ?? patient?.id ?? null
  const canLoadPatientProfile = !!session && role === 'patient'

  const profileQuery = useQuery({
    queryKey: queryKeys.patient.profile(profileQueryOwner),
    queryFn: patientProfileService.getProfile,
    enabled: canLoadPatientProfile,
  })

  const profile = profileQuery.data ?? null

  const refreshProfile = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.profile(profileQueryOwner) })
    await profileQuery.refetch()
  }, [profileQuery, profileQueryOwner, queryClient])

  const handleImageFile = async (file: File) => {
    setIsUploadingImage(true)
    const result = await patientProfileService.uploadProfileImage(file)
    setIsUploadingImage(false)
    if (result.success) {
      if (result.url) setLocalImageUrl(result.url)
      await Promise.all([refreshProfile(), refreshPatient?.()])
      window.dispatchEvent(new Event('patient-profile-updated'))
    } else {
      alert(result.error ?? 'Failed to upload photo.')
    }
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      await logout()
    } finally {
      navigate('/auth/login?role=patient', { replace: true })
    }
  }

  if (activeView === 'personal' && profile) {
    return (
      <div className="p-4 sm:p-6">
        <PersonalDetailsPanel profile={profile} onBack={() => setActiveView('main')} onSaved={() => { void refreshProfile() }} />
      </div>
    )
  }

  if (activeView === 'wallet') {
    return (
      <div className="p-4 sm:p-6">
        <WalletPanel onBack={() => setActiveView('main')} />
      </div>
    )
  }

  if (activeView === 'address') {
    return <div className="p-4 sm:p-6"><AddressPanel onBack={() => setActiveView('main')} /></div>
  }

  if (activeView === 'emergency-contacts') {
    return <div className="p-4 sm:p-6"><EmergencyContactsPanel onBack={() => setActiveView('main')} /></div>
  }

  if (activeView === 'security') {
    return <div className="p-4 sm:p-6"><SecurityPanel onBack={() => setActiveView('main')} /></div>
  }

  if (activeView === 'support') {
    return <div className="p-4 sm:p-6"><PatientSupportPanel onBack={() => setActiveView('main')} /></div>
  }

  if (activeView === 'terms') {
    return <div className="p-4 sm:p-6"><PatientTermsPanel onBack={() => setActiveView('main')} /></div>
  }

  const isLoading = patientLoading || (canLoadPatientProfile && profileQuery.isPending)
  const initials = getInitials(profile?.full_name ?? patient?.name)
  const verStatus = profile?.verification_status

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground">Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your profile settings</p>
      </div>

      {/* Profile card */}
      <div className="bg-card border border-border p-5">
        {isLoading ? (
          <div className="flex items-center gap-4 animate-pulse">
            <div className="w-16 h-16 rounded-full bg-muted shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="h-5 bg-muted rounded w-1/2" />
              <div className="h-4 bg-muted rounded w-2/3" />
              <div className="h-4 bg-muted rounded w-1/3" />
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {(localImageUrl ?? patient?.profile_image_url ?? profile?.profile_image_url) ? (
                  <img src={(localImageUrl ?? patient?.profile_image_url ?? profile?.profile_image_url)!} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-bold text-primary">{initials}</span>
                )}
                {isUploadingImage && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <button
                type="button"
                title="Upload profile photo"
                aria-label="Upload profile photo"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-card hover:bg-primary/90 transition-colors"
              >
                <Camera size={11} className="text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                title="Choose profile photo"
                aria-label="Choose profile photo"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="sr-only"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f) }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-foreground text-lg leading-tight">
                {profile?.full_name ?? patient?.name ?? '—'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {profile?.email ?? patient?.email ?? '—'}
              </p>
              <div className="flex items-center gap-1.5 mt-1.5">
                {verStatus === 'approved' ? (
                  <><Shield size={13} className="text-emerald-600" /><span className="text-xs text-emerald-600 font-medium">Identity Verified</span></>
                ) : verStatus === 'pending' ? (
                  <><Clock size={13} className="text-yellow-600" /><span className="text-xs text-yellow-600 font-medium">Verification Pending</span></>
                ) : (
                  <><Shield size={13} className="text-muted-foreground" /><span className="text-xs text-muted-foreground">Not Verified</span></>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Personal Information */}
      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Personal Information</p>
        <MenuRow icon={User} title="Personal Details" subtitle="Name, age, contact information" onClick={() => setActiveView('personal')} disabled={isLoading} />
        <MenuRow icon={MapPin} title="Address" subtitle="Manage your home address" onClick={() => setActiveView('address')} disabled={isLoading} />
        <MenuRow icon={Phone} title="Emergency Contacts" subtitle="Family and emergency contacts" onClick={() => setActiveView('emergency-contacts')} disabled={isLoading} />
      </div>

      {/* Payments */}
      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Payments</p>
        <MenuRow icon={Wallet} title="Wallet" subtitle="Manage wallet and billing" onClick={() => setActiveView('wallet')} disabled={isLoading} />
      </div>

      {/* Account & Support */}
      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Account & Support</p>
        <MenuRow icon={Shield} title="Security" subtitle="Security settings" onClick={() => setActiveView('security')} disabled={isLoading} />
        <MenuRow icon={MessageCircle} title="Support" subtitle="Get help with your patient account" onClick={() => setActiveView('support')} disabled={isLoading} />
        <MenuRow icon={FileText} title="Terms & Conditions" subtitle="Patient agreements and policies" onClick={() => navigate('/patient/terms')} disabled={isLoading} />
      </div>

      {/* Sign Out */}
      <button
        onClick={() => setShowSignOutModal(true)}
        disabled={isSigningOut}
        className="w-full flex items-center justify-center gap-2 py-3.5 border border border-destructive/30 text-destructive text-sm font-medium hover:bg-destructive/5 transition-colors disabled:opacity-60"
      >
        {isSigningOut ? (
          <div className="w-4 h-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
        ) : (
          <LogOut size={16} />
        )}
        Sign Out
      </button>

      {showSignOutModal && (
        <ConfirmationModal
          title="Sign out?"
          message="You will need to sign in again to access your patient dashboard."
          confirmLabel="Sign Out"
          confirmVariant="destructive"
          isLoading={isSigningOut}
          onConfirm={handleSignOut}
          onCancel={() => {
            if (!isSigningOut) {
              setShowSignOutModal(false)
            }
          }}
        />
      )}
    </div>
  )
}
